import { Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { useDialog } from "lib/@hooks/useDialog"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { alert } from "lib/@dialogs/alert"
import { getFentraUsers } from "routes/qa/fentra/controllers/get-fentra-users"
import { createFentraUser } from "routes/qa/fentra/controllers/fentra-mutations"
import { FentraUserRow } from "routes/qa/fentra/users/fentra-user-row"
import { FentraUserDialog } from "routes/qa/fentra/users/fentra-user-dialog"
import { FentraImportDialog } from "routes/qa/fentra/users/fentra-import-dialog"

export function FentraUsersTab() {
    const { target } = useBoundContext()
    const { _id: profileId } = target ?? {}
    const users = getFentraUsers.useResults({ profileId }) ?? []
    const openUserDialog = useDialog(<FentraUserDialog />, { maxWidth: "sm", fullWidth: true })
    const openImportDialog = useDialog(<FentraImportDialog profileId={profileId} />, {
        maxWidth: "md",
        fullWidth: true,
    })

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">Users</Typography>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={handleImport}>
                        Import from Excel
                    </Button>
                    <Button variant="contained" onClick={handleAddUser}>
                        Add User
                    </Button>
                </Stack>
            </Stack>

            <If value={users.length > 0}>
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Display Name</TableCell>
                                <TableCell>Job Title</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Provision</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat list={users} item={<FentraUserRow />} keyFn={userKeyFn} pass="user" />
                        </TableBody>
                    </Table>
                </Paper>
                <Else>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No users yet. Add one to simulate SCIM provisioning.
                    </Typography>
                </Else>
            </If>
        </Box>
    )

    function userKeyFn(u) {
        return u._id
    }

    async function handleImport() {
        await openImportDialog({ profileId })
    }

    async function handleAddUser() {
        const data = await openUserDialog({ mode: "create" })
        if (!data) return
        const result = await createFentraUser({ profileId, ...data })
        if (!result?.ok) {
            const msg = result?.scimStatus
                ? `Create failed (SCIM ${result.scimStatus}): ${result.error}`
                : `Create failed: ${result?.error ?? "Unknown error"}`
            await alert(msg, "Create User")
            return
        }
        invalidate("fentraUsers")
        successSnackbar("User created")
    }
}
